import { Text } from '@shared/components';
import { CircularProgress } from '@shared/components/plugin-components';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { ShipmentCard } from '@shared/features/shipment-card/ShipmentCard';
import {
  FindShipmentResult,
  useFindShipment,
} from '@shared/hooks/shipments/useFindShipment';
import {
  ShipmentDetails,
  useShipmentDetails,
} from '@shared/hooks/shipments/useShipmentDetails';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { useParams } from 'react-router-dom';
import ShipmentSidePanelHeader from './ShipmentSidePanelHeader';

const ROOT = makeRootClassName('ShipmentSidePanel');
export const el = makeElementClassNameFactory(ROOT);

type ShipmentSidePanelProps = {
  webBaseUrl: string;
};
export function ShipmentSidePanel({ webBaseUrl }: ShipmentSidePanelProps) {
  const { shipmentId } = useParams();
  const { defaultOrgId } = useMeContext();

  const info = useFindShipment({
    organizationId: defaultOrgId ?? '',
    shipmentId: shipmentId ?? '',
  });

  return (
    <div className={el`shipment-container`}>
      <div className={el`shipment-header`}>
        <ShipmentSidePanelHeader
          webBaseUrl={webBaseUrl}
          shipmentId={shipmentId || ''}
        />
      </div>
      <ShipmentDetailsContainer info={info} />
    </div>
  );
}

function ShipmentLoading() {
  return <CircularProgress size="sm" />;
}

type ShipmentDetailsContainerProps = {
  info: FindShipmentResult;
};
export function ShipmentDetailsContainer({
  info,
}: ShipmentDetailsContainerProps) {
  if (info.isLoading) return <ShipmentLoading />;
  if (info.error || !info.shipmentId) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2">
        <Text type="body-sm" className="p-2">
          {"Vooma doesn't have a record of this shipment."}
        </Text>
      </div>
    );
  }
  return <ShipmentDetailsComponent shipmentId={info.shipmentId} />;
}

type ShipmentDetailsComponentProps = {
  shipmentId: string;
};
function ShipmentDetailsComponent({
  shipmentId,
}: ShipmentDetailsComponentProps) {
  const info = useShipmentDetails({ shipmentId });
  return <ShipmentCardContainer info={info} />;
}

type ShipmentCardContainerProps = {
  info: ShipmentDetails;
};
function ShipmentCardContainer({ info }: ShipmentCardContainerProps) {
  if (info.isLoading) return <ShipmentLoading />;
  if (info.error || !info.shipment.id) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2">
        <Text type="body-sm" className="p-2">
          {"Vooma doesn't have a record of this shipment."}
        </Text>
      </div>
    );
  }

  const {
    id: shipmentId,
    stops,
    tmsId,
    movementPreviews,
    trackingInfo,
  } = info.shipment;
  return (
    <ShipmentCard
      referenceNumber={tmsId || ''}
      movementPreviews={movementPreviews}
      shipmentId={shipmentId}
      stops={stops}
      tmsId={tmsId}
      trackingInfo={trackingInfo ?? {}}
      showPauseControl
      refetchShipment={info.refetch}
    />
  );
}
